import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackPageView } from "@/lib/fbpixel";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import FindCustomers from "@/pages/FindCustomers";
import Dashboard from "@/pages/Dashboard";
import SavedLeads from "@/pages/SavedLeads";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import BrandFoundation from "@/pages/BrandFoundation";
import ComingSoon from "@/pages/ComingSoon";
import PaymentSuccess from "@/pages/PaymentSuccess";
import EmailLogin from "@/pages/auth/EmailLogin";
import Signup from "@/pages/auth/Signup";
import VerifyOtp from "@/pages/auth/VerifyOtp";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refund from "@/pages/legal/Refund";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import Contact from "@/pages/legal/Contact";
import InviteAccept from "@/pages/InviteAccept";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminLedger from "@/pages/admin/AdminLedger";
import AdminScans from "@/pages/admin/AdminScans";
import AdminEconomics from "@/pages/admin/AdminEconomics";
import AdminSettings from "@/pages/admin/AdminSettings";
import Features from "@/pages/marketing/Features";
import PricingPage from "@/pages/marketing/Pricing";
import RoadmapPage from "@/pages/marketing/Roadmap";
import Demo from "@/pages/marketing/Demo";
import Blog from "@/pages/marketing/Blog";
import ApiDocs from "@/pages/marketing/ApiDocs";
import About from "@/pages/marketing/About";
import "@/App.css";

function RouterInner() {
  const location = useLocation();
  // Meta Pixel PageView on initial load + every client-side navigation. Keyed
  // on pathname so in-page tab/query changes (e.g. ?lens=…) don't inflate it.
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/developers" element={<ApiDocs />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />

      {/* Legal (canonical URLs) */}
      <Route path="/privacy-policy" element={<Privacy />} />
      <Route path="/terms-of-service" element={<Terms />} />
      <Route path="/refund-policy" element={<Refund />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      {/* 301-style redirects from old legal paths */}
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
      <Route path="/refund" element={<Navigate to="/refund-policy" replace />} />

      {/* Auth */}
      <Route path="/login" element={<EmailLogin />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-otp" element={<VerifyOtp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/accept" element={<InviteAccept />} />

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="ledger" element={<AdminLedger />} />
        <Route path="scans" element={<AdminScans />} />
        <Route path="economics" element={<AdminEconomics />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      {/* App (auth-gated) */}
      <Route path="/billing/success" element={
        <ProtectedRoute><PaymentSuccess /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute><Onboarding /></ProtectedRoute>
      } />
      <Route path="/app" element={
        <ProtectedRoute><AppShell /></ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="find-customers" element={<FindCustomers />} />
        {/* The other lenses are now TABS inside Find Customers → redirect standalone paths there */}
        <Route path="pain-points" element={<Navigate to="/app/find-customers?lens=pain_points" replace />} />
        <Route path="market" element={<Navigate to="/app/find-customers?lens=market" replace />} />
        <Route path="partners" element={<Navigate to="/app/find-customers?lens=partners" replace />} />
        <Route path="saved-leads" element={<SavedLeads />} />
        <Route path="brand" element={<BrandFoundation />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouterInner />
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              color: "#1A1A1A",
              border: "1px solid #E8E6E1",
              borderRadius: "12px",
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
